import {
  Accordion as AccordionBase,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/common/components/ui/accordion";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface AccordionProps {
  name?: string;
  head: JSX.Element | string;
  body: JSX.Element | string;
  className?: string;
  iconStyles?: string;
  triggerClassName?: string;
  triggerDisabled?: boolean;
  contentClassName?: string;
  bodyClassName?: string;
  dataTestid?: string;
  onToggle?: (isOpen: boolean) => void;
  defaultOpen?: boolean;
  open?: boolean;
}

const Accordion = ({
  name,
  head,
  body,
  className,
  iconStyles,
  triggerClassName,
  triggerDisabled,
  contentClassName,
  bodyClassName,
  onToggle,
  dataTestid,
  defaultOpen = false,
  open,
}: AccordionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(defaultOpen ? "item-1" : "");

  const defaultOnToggle = (isOpen: boolean) => {
    if (!contentRef.current) return;
    if (isOpen) {
      (contentRef.current as HTMLDivElement).classList.remove("hidden");
      setTimeout(() => { if (contentRef.current) contentRef.current.classList.add("overflow-visible") }, 300);
    } else {
      setTimeout(() => { if (contentRef.current) contentRef.current.classList.add("hidden") }, 100);
      contentRef.current.classList.remove("overflow-visible");
    }
  };

  const handleOnToggle = (itemName: string) => {
    setValue(itemName);
    onToggle && onToggle(!!itemName);
  }

  useEffect(() => {
    defaultOnToggle(!!value);
  }, [value])

  useEffect(() => {
    if (defaultOpen && contentRef.current) {
      contentRef.current.classList.add("overflow-visible");
    }
  }, [defaultOpen, contentRef.current?.id])

  useEffect(() => {
    if (open !== undefined) {
      setValue(open ? 'item-1' : '');
    }
  }, [open]);

  return (
    <AccordionBase
      type="single"
      collapsible
      className={twMerge("w-full p-4 rounded-sm bg-white-100", className)}
      onValueChange={handleOnToggle}
      value={value}
      data-testid={dataTestid || `accordion_${name}`}
    >
      <AccordionItem value="item-1">
        <AccordionTrigger
          className={twMerge("py-4", triggerClassName)}
          disabled={triggerDisabled}
          iconStyles={iconStyles}
          data-testid={`accordion_${name}_trigger`}
        >
          {head}
        </AccordionTrigger>
        <AccordionContent ref={contentRef} containerClassName={contentClassName} className={bodyClassName}>
          {body}
        </AccordionContent>
      </AccordionItem>
    </AccordionBase>
  );
};

export default Accordion;
